import {
    Component,
    Input,
    OnInit,
    QueryList,
    Renderer2,
    RendererFactory2,
    ViewChild,
    ViewChildren,
    OnDestroy,
    DOCUMENT,
    inject,
    ElementRef
} from '@angular/core';
import {
    AbstractControl,
    ControlValueAccessor,
    FormControl,
    NG_VALIDATORS,
    NG_VALUE_ACCESSOR,
    FormGroup,
    ValidationErrors,
    Validator,
    Validators,
    ReactiveFormsModule
} from '@angular/forms';
import { MatSelect, MatOption } from '@angular/material/select';
import { DropdownErrorMatcher, SearchDropdown, searchDropdownOptionsValidator } from '@nx-customer-apps/shared/utils';
import { ViewHostDirective } from '../../directives/view-host.directive';
import { debounceTime, delay, distinctUntilChanged, map, switchMap, tap, Subject, takeUntil } from 'rxjs';
import { ComponentsModule, slideDown } from '@nx-customer-apps/components';
import { SearchDropdownItem } from '@nx-customer-apps/shared/interfaces';
import { MatFormField, MatLabel, MatSuffix, MatHint, MatError } from '@angular/material/form-field';
import { NgClass } from '@angular/common';
import { MatInput } from '@angular/material/input';
import { NgxMaskDirective } from 'ngx-mask';
import { TranslatePipe } from '@ngx-translate/core';
import { FormFieldStylesPipe } from '../../pipes';
import { IsNilPipe } from '@nx-customer-apps/components';

@Component({
    selector: 'vp-search-dropdown',
    templateUrl: './search-dropdown.component.html',
    styleUrls: ['./search-dropdown.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: SearchDropdownComponent
        },
        {
            provide: NG_VALIDATORS,
            multi: true,
            useExisting: SearchDropdownComponent
        }
    ],
    animations: [slideDown],
    imports: [
        MatFormField,
        NgClass,
        MatLabel,
        MatInput,
        ReactiveFormsModule,
        NgxMaskDirective,
        MatSelect,
        ComponentsModule,
        MatOption,
        ViewHostDirective,
        MatSuffix,
        MatHint,
        MatError,
        TranslatePipe,
        FormFieldStylesPipe,
        IsNilPipe
    ]
})
export class SearchDropdownComponent implements ControlValueAccessor, Validator, OnInit, OnDestroy {
    private document = inject<Document>(DOCUMENT);

    @Input() highlight: boolean;
    @Input() question: SearchDropdown;
    /**
     * The direct form group the question belongs to.
     */
    @Input() formGroup: FormGroup;
    @Input() minimalSearchValueLength = 3;
    @ViewChild('dropdownRef') dropdownRef: MatSelect;
    @ViewChild('inputRef') inputRef: ElementRef;
    @ViewChild('optionsContainerRef') optionsContainerRef: ElementRef;
    @ViewChildren(ViewHostDirective) optionHosts: QueryList<ViewHostDirective>;
    public dropdownControl = new FormControl();
    public textControl = new FormControl();
    public matcher = new DropdownErrorMatcher();
    public value: any;
    public loading: boolean;
    private renderer: Renderer2;
    private destroy$ = new Subject<void>();

    constructor() {
        const rendererFactory = inject(RendererFactory2);

        this.renderer = rendererFactory.createRenderer(null, null);
    }

    public ngOnInit(): void {
        this.openDropDown();
        this.initializeValue();
        this.setValidators(this.question.options);
        this.setupParentFormControlSync();
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private setupParentFormControlSync(): void {
        if (!this.control) {
            return;
        }

        // Initial sync
        this.syncValidationState();

        // Monitor parent control changes more directly
        const originalMarkAsTouched = this.control.markAsTouched.bind(this.control);
        this.control.markAsTouched = (options?: any) => {
            originalMarkAsTouched(options);
            this.syncValidationState();
        };

        // Also subscribe to status changes
        this.control.statusChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
            this.syncValidationState();
        });
    }

    private syncValidationState(): void {
        setTimeout(() => {
            if (this.control.touched && !this.textControl.touched) {
                this.textControl.markAsTouched();
                this.onTouched(); // Notify the parent form
            }
            // If parent control has validation errors, ensure textControl also shows them
            if (this.control.invalid && this.control.touched) {
                // Always set the error to ensure proper display
                this.textControl.setErrors({ message: this.question.defaultErrorMessage });
                this.textControl.markAsTouched();
                this.onTouched(); // Notify the parent form
            }
        });
    }

    public writeValue(value: any): void {
        this.value = value;
        if (!value) {
            this.textControl.setValue(value, { emitEvent: false });
        }
    }

    /* istanbul ignore next */
    public onChange: (...args: any) => void = (value: number | '') => {};

    /* istanbul ignore next */
    public registerOnChange(onChange: any): void {
        this.onChange = onChange;
    }

    /* istanbul ignore next */
    public onTouched: (...args: any) => void = () => {};

    /* istanbul ignore next */
    public registerOnTouched(onTouched: (...args: any) => void): void {
        this.onTouched = (...args: any) => {
            // Call the original onTouched callback
            onTouched(...args);
            // Also sync validation state when touched
            this.syncValidationState();
        };
    }

    public validate(control: AbstractControl): ValidationErrors | null {
        // If textControl has errors, return those
        if (this.textControl && this.textControl.errors) {
            return this.textControl.errors;
        }

        // If the textControl is empty and required validator exists, return required error
        const value = this.textControl?.value;
        if (
            this.question.validators.some(v => v === Validators.required) &&
            (!value || (typeof value === 'string' && value.trim() === ''))
        ) {
            return { required: true };
        }

        return null;
    }

    /* istanbul ignore next */
    public onValidationChange: (...args: any) => void = () => {};

    /* istanbul ignore next */
    public registerOnValidatorChange?(onValidationChange: (...args: any) => void): void {
        this.onValidationChange = onValidationChange;
    }

    public get isInvalid(): boolean {
        // If parent control is invalid and touched, the component should show as invalid
        const parentInvalid = this.control.invalid && this.control.touched;
        const textInvalid = this.textControl?.invalid && this.textControl?.touched;
        return parentInvalid || textInvalid;
    }

    public get control(): AbstractControl {
        return this.formGroup!.get(this.question.key)!;
    }

    public get isTouched(): boolean {
        return this.control.touched || this.textControl.touched;
    }

    public get errorMessage(): string {
        const errorMessage = this.textControl?.errors?.['message'];
        return errorMessage || this.question.defaultErrorMessage;
    }

    public openDropDown(): void {
        this.textControl.valueChanges
            .pipe(
                takeUntil(this.destroy$),
                map(value => value.trim()),
                map(value => (value.length < this.minimalSearchValueLength ? '' : value)),
                debounceTime(300),
                distinctUntilChanged(),
                tap(value => {
                    if (value) {
                        this.tooglefBackdropClickEvents(true);
                        this.loading = true;
                        this.dropdownRef.open();
                    }
                }),
                switchMap(value => this.question.optionsUpdater(value)),
                tap(newOptions => {
                    this.question.options = newOptions.length ? newOptions : this.question.defaultOptions;
                    this.setValidators(newOptions);
                    this.textControl.updateValueAndValidity();
                    this.tooglefBackdropClickEvents(false);
                }),
                tap(() => {
                    this.loading = false;
                }),
                delay(0),
                tap(() => {
                    this.loadOptionTemplates();
                    this.dropdownRef.open();
                })
            )
            .subscribe();
    }

    private setValidators(options: SearchDropdownItem[]): void {
        const validators = [...this.question.validators, searchDropdownOptionsValidator(options, this.question.defaultErrorMessage)];
        this.textControl.setValidators(validators);
    }

    public onDropdownSelect(event: { value: any }): void {
        this.textControl.setValue(event.value, { emitEvent: false });
        const selectedOption = this.question.options.find(option => option.key === event.value)!;
        this.writeValue(selectedOption.value);
        this.onChange(selectedOption.value);
        this.dropdownControl.reset();
    }

    public onFocus(): void {
        /**
         * Workaround! options container is adjacent "before end" of the mat-select tag
         * at the first dropdown opening which causes buggy behavior (dropdown is snapped and does not close itself).
         * checking if options are inside mat-select prevents the bug and allows smooth work.
         *
         * The right way to fix the issue is to migrate this component to angular material autocomplete component in the future.
         */
        const isInsideMatSelect = (this.optionsContainerRef.nativeElement as HTMLElement).closest('mat-select') !== null;

        if (this.textControl.value && this.question.options.length > 0 && !isInsideMatSelect) {
            this.loadOptionTemplates();
            this.dropdownRef.open();
        }
        /**
         *  Workaround! Blurring the input helps to open the dropdown list on the second time when user clicks the input.
         */
        if (isInsideMatSelect) {
            (this.inputRef.nativeElement as HTMLElement).blur();
        }
    }

    /**
     * Generates custom templates for dropdown options if they're defined in the this.question.options.
     */
    private loadOptionTemplates(): void {
        this.question.options.forEach((option, index) => {
            if (!option.customTemplate) {
                return;
            }
            const host = this.optionHosts.get(index);
            if (host) {
                host.viewContainerRef.clear();
                const componentRef = host.viewContainerRef.createComponent(option.customTemplate.component);
                if (componentRef) {
                    const componentInputs = option.customTemplate.inputs;
                    for (const key in componentInputs) {
                        (<any>componentRef).instance[key] = componentInputs[key];
                    }
                }
            }
        });
    }

    /**
     * Workaround! It prevents the dropdown from closing on "click outside" event while the options are being loaded [CAVP-1031]
     */
    private tooglefBackdropClickEvents(isActive: boolean): void {
        isActive
            ? this.renderer.addClass(this.document.body, 'vp-cdk-backdrop-pointer-events-none')
            : this.renderer.removeClass(this.document.body, 'vp-cdk-backdrop-pointer-events-none');
    }

    private initializeValue(): void {
        const selectedOption = this.question.options.find(option => option.value === this.question.value);
        this.textControl.setValue(selectedOption?.key, { emitEvent: false });
        if (this.question.disabled) {
            this.textControl.disable({ emitEvent: false });
            this.dropdownControl.disable({ emitEvent: false });
        }
    }
}
